import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

type Segment = { label: string; value: number; className: string };

function clampPct(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

export function KpiCard(props: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "neutral" | "good" | "warn" | "danger";
}) {
  const tone = props.tone ?? "neutral";
  const toneClass =
    tone === "good"
      ? "bg-emerald-50 text-emerald-950 ring-emerald-200/80"
      : tone === "warn"
        ? "bg-amber-50 text-amber-950 ring-amber-200/80"
        : tone === "danger"
          ? "bg-rose-50 text-rose-950 ring-rose-200/80"
          : "bg-white text-brand-blue ring-black/10";

  return (
    <div className={["rounded-2xl p-4 shadow-sm ring-1", toneClass].join(" ")}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-70">{props.label}</p>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <p className="font-display text-3xl font-bold leading-none">{props.value}</p>
      </div>
      {props.hint ? <p className="mt-2 text-xs font-semibold opacity-70">{props.hint}</p> : null}
    </div>
  );
}

export function StackedBar(props: { segments: Segment[]; title: string; subtitle?: string }) {
  const total = props.segments.reduce((a, s) => a + (s.value ?? 0), 0);
  const sorted = [...props.segments].sort((a, b) => b.value - a.value);

  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/10">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold">{props.title}</p>
          {props.subtitle ? <p className="mt-1 text-xs font-semibold text-brand-blue/60">{props.subtitle}</p> : null}
        </div>
        <p className="text-xs font-bold text-brand-blue/50">{total}</p>
      </div>

      <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-brand-gray/40 ring-1 ring-black/5">
        <div className="flex h-full w-full">
          {sorted.map((s) => {
            const pct = total ? clampPct((s.value / total) * 100) : 0;
            return <div key={s.label} className={s.className} style={{ width: `${pct}%` }} />;
          })}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3">
        {sorted.map((s) => {
          const pct = total ? Math.round((s.value / total) * 100) : 0;
          return (
            <div key={s.label} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex min-w-0 items-center gap-2">
                <span className={["h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10", s.className].join(" ")} />
                <span className="min-w-0 truncate font-semibold text-brand-blue/75">{s.label}</span>
              </div>
              <span className="shrink-0 font-bold text-brand-blue/55">
                {s.value} · {pct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PercentPills(props: { title: string; items: { label: string; value: number; tone: Segment["className"] }[] }) {
  const total = props.items.reduce((a, i) => a + (i.value ?? 0), 0);
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/10">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-bold">{props.title}</p>
        <p className="text-xs font-bold text-brand-blue/50">{total}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {props.items.map((i) => {
          const pct = total ? Math.round((i.value / total) * 100) : 0;
          return (
            <span
              key={i.label}
              className={["inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1", i.tone].join(
                " "
              )}
              title={`${i.value} (${pct}%)`}
            >
              <span className="opacity-80">{i.label}</span>
              <span className="opacity-80">{pct}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function SectionHeader(props: { title: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div>
        <p className="font-display text-2xl font-bold">{props.title}</p>
      </div>
      {props.right ? <div className="shrink-0">{props.right}</div> : null}
    </div>
  );
}

export function MiniTable(props: { title: string; subtitle?: string; rows: React.ReactNode; footerHref?: string }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/10">
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div>
          <p className="text-sm font-bold">{props.title}</p>
          {props.subtitle ? <p className="mt-1 text-xs font-semibold text-brand-blue/60">{props.subtitle}</p> : null}
        </div>
      </div>
      <div className="mt-3 divide-y divide-black/5">{props.rows}</div>
      {props.footerHref ? (
        <Link
          to={props.footerHref}
          className="flex items-center justify-between gap-2 border-t border-black/5 px-4 py-3 text-xs font-bold text-brand-blue/70 hover:bg-brand-gray/15"
        >
          <span>View all</span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
}

